import MatrixItem from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/MatrixItem'
import MatrixRow from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/MatrixRow'
import QuestionItem from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/QuestionItem'
import TextCardItem from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/TextCardItem'
import {
  ConjointItem,
  ConjointPackage,
  ConjointPanel,
  PackageAttributeLevel
} from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Conjoint/ConjointItem.types'
import { TextHighlighterItem } from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/TextHighlighter/TextHighlighter.types'
import { ToWords } from 'to-words'
import { v4 as uuid } from 'uuid'
import {
  DraftConjointItem,
  DraftMatrixRow
} from '../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  EntryType,
  MatrixTitle,
  MediaType,
  Question,
  QuestionSettingCode
} from '../../data/model/questionnaire'
import {
  getChoiceIntervalMax,
  getChoiceIntervalMin,
  getChoiceLimit,
  getQuestionEnabledSettings,
  getRankChoiceRequisite,
  getResponseOptions
} from '../../utils/questionnaireUtils'
import { createSeededRandom } from '../../utils/randomUtils'
import useDebounce from '../useDebounce'

interface Loc {
  next: string
  instructions: string
}
interface MatrixLoc {
  next: string
  matrixInstructions: {
    singleSelectDesktop: string
    multiSelectDesktop: string
    singleSelectMobile: string
    multiSelectMobile: string
  }
}

interface MatrixLoc {
  next: string
  matrixInstructions: {
    singleSelectDesktop: string
    multiSelectDesktop: string
    singleSelectMobile: string
    multiSelectMobile: string
  }
}

interface RankedLoc {
  next: string
  instructions: string
  tapToRemove: string
  dragToReorder: string
  tapToSelect: string
}

interface FreeTextLoc {
  next: string
  instructions: string
  typeYourAnswerHere: string
  characterLimit: string
}

export const loc = { next: 'Next', instructions: 'Select one' }

export const scaleLoc = {
  next: 'Next',
  instructions: 'Drag the button to rate'
}

const convertNumberToWord: (number: number) => string = (number) => {
  const toWords = new ToWords({
    localeCode: 'en-IN',
    converterOptions: {
      currency: false,
      ignoreDecimal: false,
      ignoreZeroCurrency: false
    }
  })
  const word = toWords.convert(number).toLowerCase()
  return word
}
const getInstructions: (
  choiceLimit?: number,
  min?: number,
  max?: number,
  responseOptionCount?: number
) => string = (choiceLimit, min, max, responseOptionCount) => {
  if (min !== undefined && max) {
    if (min === max) return `Select ${choiceLimit || max} answers`
    if (min !== 0) return `Select between ${min} and ${max} answers`
  }
  return choiceLimit ||
    (min === 0 && max && responseOptionCount && max < responseOptionCount)
    ? `Select up to ${convertNumberToWord(choiceLimit || max || 0)} answers`
    : 'Select all that apply'
}

export const getRankedLoc: (entryItem: DraftQuestionItem) => RankedLoc = (
  entryItem
) => {
  const defaultRankChoiceRequisiteOption = {
    id: '0',
    name: 'All ranked',
    value: 0
  }

  const rankChoiceRequisite = getRankChoiceRequisite(entryItem)
  const defaultRankChoiceRequisiteValue = rankChoiceRequisite
    ? parseInt(rankChoiceRequisite, 10)
    : defaultRankChoiceRequisiteOption.value

  return {
    next: 'Next',
    instructions: getInstructions(defaultRankChoiceRequisiteValue),
    tapToRemove: 'TAP TO REMOVE',
    dragToReorder: 'DRAG TO REORDER',
    tapToSelect: 'TAP TO SELECT'
  }
}

export const getFreeTextLoc: () => FreeTextLoc = () => {
  return {
    next: 'Next',
    instructions: 'Enter your answer in the box below',
    typeYourAnswerHere: 'Type your answer here...',
    characterLimit: 'characters remaining'
  }
}

export const getMultiChoiceLoc: (entryItem: DraftQuestionItem) => Loc = (
  entryItem
) => {
  const defaultOption = {
    id: '0',
    name: 'No limit',
    value: 0
  }

  const choiceLimit = getChoiceLimit(entryItem)
  const choiceIntervalMin = getChoiceIntervalMin(entryItem)
  const choiceIntervalMax = getChoiceIntervalMax(entryItem)
  const responseOptionCount = entryItem.responseOptions.length

  const defaultOptionValue = choiceLimit
    ? parseInt(choiceLimit, 10)
    : defaultOption.value

  return {
    next: 'Next',
    instructions: getInstructions(
      defaultOptionValue,
      choiceIntervalMin,
      choiceIntervalMax,
      responseOptionCount
    )
  }
}

export const getMatrixMultiChoiceLoc: (
  entryItem: DraftMatrixItem
) => MatrixLoc = (entryItem) => {
  const defaultOption = {
    id: '0',
    name: 'No limit',
    value: 0
  }

  const choiceLimit = getChoiceLimit(entryItem)
  const choiceIntervalMin = getChoiceIntervalMin(entryItem)
  const choiceIntervalMax = getChoiceIntervalMax(entryItem)
  const responseOptionCount = entryItem.responseOptions.length

  const defaultOptionValue = choiceLimit
    ? parseInt(choiceLimit, 10)
    : defaultOption.value

  return {
    next: 'Next',
    matrixInstructions: {
      singleSelectDesktop: 'Select one answer per row',
      multiSelectDesktop: `${getInstructions(
        defaultOptionValue,
        choiceIntervalMin,
        choiceIntervalMax,
        responseOptionCount
      )} per row`,
      singleSelectMobile: 'Select one answer',
      multiSelectMobile: getInstructions(
        defaultOptionValue,
        choiceIntervalMin,
        choiceIntervalMax,
        responseOptionCount
      )
    }
  }
}

export const getTextInstructionsLoc = (entryItem: TextCardItem): Loc => {
  return {
    next: 'Next',
    instructions: entryItem.textCard.body
  }
}

export const getConjointLoc = (): Loc => {
  return {
    next: 'Next',
    instructions: 'Please select one package from each panel'
  }
}

const useTransformDraftQuestionnaireToPreviewData = (
  draftQuestionnaireEntries: DraftQuestionnaireEntry[] | undefined
) => {
  const debouncedQuestionnaireEntries = useDebounce(
    draftQuestionnaireEntries,
    500
  )

  const getMatrixRows = (draftMatrixRows: DraftMatrixRow[]): MatrixRow[] => {
    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return draftMatrixRows?.map((draftMatrixRow) => {
      const matrixRow: MatrixRow = {
        position: draftMatrixRow.position,
        renderedPosition: draftMatrixRow.position,
        question: {
          questionId: draftMatrixRow.question?.questionId ?? '',
          text: draftMatrixRow.question?.text ?? ''
        },
        questionMedia: {
          mediaType: draftMatrixRow.questionMedia?.mediaType || MediaType.Image,
          mediaUrl: draftMatrixRow.questionMedia?.mediaUrl || '',
          renderedMediaUrl: draftMatrixRow.questionMedia?.renderedMediaUrl ?? ''
        }
      }
      return matrixRow
    })
  }

  // Create a deterministic seed based on conjoint item properties
  const createSeed = (draftConjointItem: DraftConjointItem): string => {
    const attributesHash = draftConjointItem.attributes
      .map(
        (attr) =>
          `${attr.attributeId}:${attr.name}:${attr.options
            .map((opt) => `${opt.attributeOptionId}:${opt.value}`)
            .join(',')}`
      )
      .join('|')

    const settingsHash =
      draftConjointItem.settingValues
        .map((sv: any) => `${sv.code}:${sv.value}`)
        .sort()
        .join('|') || ''

    return `${draftConjointItem.questionTypeCode}:${attributesHash}:${settingsHash}`
  }

  const generateConjointPanels = (
    draftConjointItem: DraftConjointItem
  ): ConjointPanel[] => {
    // Get settings for panel size and number of panels
    const panelSizeSetting = draftConjointItem.settingValues.find(
      (sv: any) => sv.code === QuestionSettingCode.ConjointPanelSize
    )
    const numberOfPanelsSetting = draftConjointItem.settingValues.find(
      (sv: any) => sv.code === QuestionSettingCode.ConjointNumberOfPanels
    )

    const panelSize = panelSizeSetting
      ? parseInt(panelSizeSetting.value, 10)
      : 2 // default 2 packages per panel
    const numberOfPanels = numberOfPanelsSetting
      ? parseInt(numberOfPanelsSetting.value, 10)
      : 5 // default 5 panels

    const panels: ConjointPanel[] = []

    for (let panelIndex = 0; panelIndex < numberOfPanels; panelIndex++) {
      const packages: ConjointPackage[] = []

      for (let packageIndex = 0; packageIndex < panelSize; packageIndex++) {
        const packageAttributeLevels: PackageAttributeLevel[] = []

        // Create seeded random generator for consistent results
        const seed = createSeed(draftConjointItem)
        const seededRandom = createSeededRandom(
          `${seed}:${panelIndex}:${packageIndex}`
        )

        // For each attribute, randomly select an option using seeded random —
        // restricted to options eligible for this package. Backend semantics:
        // allowedPackages == null means unrestricted (eligible everywhere);
        // [] means eligible nowhere (an allow-list with nothing on it allows
        // nothing) — must NOT be treated as unrestricted here, or preview
        // shows attributes respondents will never see. If a package has no
        // eligible option for an attribute (a mis-configured survey that
        // should be blocked by validation before launch), skip that
        // attribute's row entirely — matching the respondent renderer, which
        // does the same rather than falling back to the full option list.
        // Validation is FE-only, so an API/Forge-built survey can still reach
        // this state; the preview must show what respondents actually see.
        draftConjointItem.attributes.forEach(
          (attribute: any, attrIndex: number) => {
            const eligibleOptions = attribute.options.filter((option: any) => {
              const allowedPackages = option.allowedPackages
              return (
                allowedPackages == null ||
                allowedPackages.includes(packageIndex)
              )
            })

            if (eligibleOptions.length === 0) {
              return
            }

            const randomOptionIndex = Math.floor(
              seededRandom() * eligibleOptions.length
            )
            const selectedOption = eligibleOptions[randomOptionIndex]

            // attributeOptionMedia/isAppearance are pending the
            // @focaldata/cin-ui-components release documented in the
            // appearance-images contract (not yet in PackageAttributeLevel's
            // published types) — cast until package.json is bumped.
            packageAttributeLevels.push({
              position: attrIndex + 1,
              attributeId: attribute.attributeId,
              attributeName: attribute.name,
              attributeOptionId: selectedOption.attributeOptionId,
              attributeOptionValue: selectedOption.value,
              attributeOptionMedia: selectedOption.media
                ? {
                    mediaUrl: selectedOption.media.mediaUrl,
                    mediaType: selectedOption.media.mediaType,
                    renderedMediaUrl: selectedOption.media.renderedMediaUrl
                  }
                : null,
              isAppearance: attribute.isAppearance ?? false
            } as PackageAttributeLevel)
          }
        )

        packages.push({
          conjointPackageId: `package_${panelIndex}_${packageIndex}`,
          // Backend package positions are 0-based (allowedPackages indexes
          // match packageIndex directly) — +1 here would shift every package
          // label and restriction off by one card in preview.
          position: packageIndex,
          packageAttributeLevels
        })
      }

      panels.push({
        conjointPanelId: `panel_${panelIndex}`,
        position: panelIndex + 1,
        packages
      })
    }

    return panels
  }

  const getPreviewEntries = (
    draftQuestionnaireEntries: DraftQuestionnaireEntry[]
  ) => {
    return draftQuestionnaireEntries.reduce<
      {
        item:
          | QuestionItem
          | MatrixItem
          | TextCardItem
          | TextHighlighterItem
          | ConjointItem
        id: string
      }[]
    >((acc, entry) => {
      if (entry.entryType === EntryType.QuestionEntryType) {
        const entryItem = entry.entryItem
        const {
          question = {} as Question,
          questionMedia,
          questionScale,
          questionTypeCode,
          settingValues
        } = entryItem
        const item: QuestionItem = {
          question: {
            ...question,
            titleStyling: question.textStyling ?? undefined
          },
          questionMedia,
          questionScale,
          questionTypeCode,
          responseOptions: getResponseOptions(
            entryItem,
            entryItem.responseOptions,
            {
              showInitialValues: true
            }
          ),
          settingValues,
          number: entry.number
        }
        return [...acc, { item, id: entry.id }]
      }
      if (entry.entryType === EntryType.MatrixEntryType) {
        const entryItem = entry.entryItem
        const {
          matrixTitle = {} as MatrixTitle,

          settingValues
        } = entryItem

        const item: MatrixItem = {
          matrixTitle: {
            ...matrixTitle,
            titleStyling: matrixTitle.titleStyling ?? undefined
          },
          matrixRows: getMatrixRows(entryItem.matrixRows),
          responseOptions: getResponseOptions(
            entryItem,
            entryItem.responseOptions,
            {
              showInitialValues: true
            }
          ),
          settingValues,
          number: entry.number
        }
        return [...acc, { item, id: entry.id }]
      }
      if (entry.entryType === EntryType.TextCardEntryType) {
        const entryItem = entry.entryItem
        const item: TextCardItem = {
          textCard: {
            textCardId: entryItem.textCardLk,
            title: entryItem.textCard.title,
            titleStyling: entryItem.textCard.titleStyling ?? undefined,
            subtitle: entryItem.textCard.subtitle,
            body: entryItem.textCard.body
          },
          questionMedia: entryItem.questionMedia,
          number: entry.number
        }
        return [...acc, { item, id: entry.id }]
      }
      if (entry.entryType === EntryType.TextHighlighterEntryType) {
        const entryItem = entry.entryItem
        const item: TextHighlighterItem = {
          content: {
            ...entryItem.content,
            textStyling: entryItem.content.textStyling ?? undefined
          },
          question: {
            ...entryItem.question,
            text: entryItem.question?.text ?? '',
            titleStyling: entryItem.question?.textStyling ?? undefined,
            questionId: entryItem.question?.questionId ?? ''
          },
          questionTypeCode: 'text_highlighter',
          settingValues: entryItem.settingValues,
          categories: entryItem.highlighterCategories.map((category) => ({
            colour: category.colour.hex,
            option: {
              isDefaultOption: false,
              isExclusiveOption: false,
              isNumericEntry: false,
              isQualifying: true,
              isTextEntry: false,
              responseOptionId: category.responseOption.responseOptionId,
              roType: 3, // String
              value: category.responseOption.value
            },
            position: category.position,
            renderedPosition: category.position
          }))
        }

        return [...acc, { item, id: entry.id }]
      }
      if (entry.entryType === EntryType.ConjointEntryType) {
        const entryItem = entry.entryItem as DraftConjointItem
        const {
          question = {} as Question,
          questionMedia,
          settingValues
        } = entryItem

        const panels = generateConjointPanels(entryItem)

        // Check if none_of_these setting is enabled
        const enabledSettings = getQuestionEnabledSettings(settingValues)
        const defaultResponseOption = enabledSettings.has(
          QuestionSettingCode.NoneOfThese
        )
          ? {
              position: panels.length + 1,
              option: {
                responseOptionId: uuid(),
                value: 'None of these',
                isQualifying: false,
                isDefaultOption: true,
                isExclusiveOption: true,
                isNumericEntry: false,
                isTextEntry: false,
                roType: 3
              },
              renderedPosition: panels.length + 1
            }
          : undefined

        const item: ConjointItem = {
          questionTypeCode: entryItem.questionTypeCode,
          question: {
            questionId: question?.questionId || '',
            text: question?.text || '',
            titleStyling: question?.textStyling ?? undefined
          },
          questionMedia: questionMedia
            ? {
                mediaUrl: questionMedia.mediaUrl || '',
                mediaType: questionMedia.mediaType,
                renderedMediaUrl: questionMedia.renderedMediaUrl || undefined
              }
            : undefined,
          panels,
          settingValues,
          defaultResponseOption
        }

        return [...acc, { item, id: entry.id }]
      }
      return acc
    }, [])
  }
  const previewEntries = getPreviewEntries(debouncedQuestionnaireEntries ?? [])
  return {
    previewEntries
  }
}

export default useTransformDraftQuestionnaireToPreviewData
